/**
 * Dated scoring-methodology notices.
 *
 * When a scoring rule changes, a customer's number can move without anything
 * on their site changing. That is confusing at best and looks like a bug at
 * worst, so every such change ships WITH its explanation, attached to the
 * score itself rather than buried in a changelog nobody opens.
 *
 * This file is the editable source of that copy — edit the strings here, not
 * the component. `/platform/methodology` renders the same list as the
 * canonical archive, so the in-product note and the reference page can never
 * drift apart.
 */

export interface ScoringNotice {
  /** Stable id — also the anchor on the methodology page. */
  id: string
  /** ISO date the change went live. */
  date: string
  /** Human date as shown in the heading. */
  displayDate: string
  title: string
  /** Rendered as separate paragraphs, in order. */
  body: string[]
  /** Which tools' result surfaces should carry the notice. */
  tools: string[]
}

export const SCORING_NOTICES: ScoringNotice[] = [
  {
    // PIX-177/181. Ship date: MethodologyRef's changelog entry, section 10 of
    // methodology.claims.md and METHODOLOGY_DATE_MODIFIED carry the same date.
    id: 'balanced-sentiment-thread-quotes-2026-10-17',
    date: '2026-10-17',
    displayDate: '17 October 2026',
    title: 'Balanced comparisons and Reddit quote ownership',
    body: [
      'Citation Check treats balanced pros-and-cons comparisons as cautionary unless the answer clearly recommends or rejects your brand. This clarification can change sentiment points without a change to your site. The classifier also receives up to 6,000 characters per answer instead of 400, so later caveats are less likely to be cut off. Sentiment weights and regex fallback rules are unchanged.',
      'Reddit Monitor’s backup search keeps a quote only when it matches wording in a source block tied to that same thread. Quotes with ambiguous ownership are dropped. This can change sentiment and promotional-content signals, and may remove mentions that no longer have enough evidence to identify your brand. The main search path is unchanged.',
      'Saved runs retain their original scores. Compare results with these methodology changes in mind; no fixed score adjustment applies to every brand.',
    ],
    tools: ['citation-check', 'reddit-monitor'],
  },
  {
    // PIX-173. Ship date: MethodologyRef's changelog entry, section 9 of
    // methodology.claims.md and METHODOLOGY_DATE_MODIFIED carry the same date.
    id: 'chatgpt-gpt-6-luna',
    date: '2026-10-16',
    displayDate: '16 October 2026',
    title: 'ChatGPT results now use gpt-6-luna',
    body: [
      "OpenAI retires gpt-4o-mini, the model behind Radar's ChatGPT results, on 14 January 2027 and names gpt-6-luna as its replacement. From this date, new Radar runs use gpt-6-luna to collect ChatGPT's answers, to identify your brand and its category, and to judge parts of several scores: sentiment, which brands in an answer are competitors, and whether facts about your brand are correct. Your category shapes the questions Prompt SOV asks, so a different category can mean different questions. In our matched tests on one brand, OpenAI, the model change alone moved scores by 4 points or less and changed no grade.",
      "Testing the change also exposed four problems, now fixed. Prompt SOV no longer counts a brand's own products, add-ons or sub-brands as its competitors, or technologies an answer mentions only as background. Source Influence ignores large platforms such as google.com and github.com, and that filter also removed the audited site when it was one of them; the audited site is no longer filtered out. In Citation Check, Prompt SOV, Source Influence and Answer Engine, ChatGPT answers that were cut off, refused, empty or otherwise incomplete were scored as if complete; they are now treated like a failed request, and those answers get more room. Reddit Monitor's backup search now keeps a quote only when it matches the source word for word, and counts each thread once.",
      'Together, the fixes and the model change can move a score with no change on your site. On our test brand, OpenAI, Prompt SOV rose from about 80 to about 90 and Source Influence from about 77 to about 85, each moving from B to A, and none of 48 ChatGPT answers was cut off, against 21 before. Most of that rise came from the fixes: with the fixes on the old model, the two scores were already about 90 and 85. These are results for one test brand, not an adjustment applied to every score. Saved runs keep their original scores and are not regenerated, so runs from before and after this date are not directly comparable.',
    ],
    tools: [
      'citation-check',
      'prompt-sov',
      'source-influence',
      'answer-engine',
      'hallucination-check',
      'brand-disambiguation',
      'reddit-monitor',
      'youtube-monitor',
    ],
  },
  {
    id: 'schema-audit-v2-2026-08-03',
    date: '2026-08-03',
    displayDate: '3 August 2026',
    title: 'Schema Audit scoring methodology v2',
    body: [
      'Schema Audit now scores business identity, WebSite, navigation context, and a page type appropriate to the audited content. Article, FAQPage, and SpeakableSpecification are no longer universal requirements, and ProfessionalService, Place, and CreativeWork now have field-completeness rubrics.',
      'Volume now counts typed schema entities rather than type memberships described as JSON-LD blocks, and volume and evaluated-type diversity build one point at a time instead of maxing out at five. If your score changed without a site update, compare runs by scoring-method version; version 1 and version 2 are not directly comparable.',
    ],
    tools: ['schema-audit', 'platform-dashboard'],
  },
  {
    id: 'withheld-tools-excluded-2026-07-20',
    date: '2026-07-20',
    displayDate: '20 July 2026',
    title: 'Withheld checks no longer count as zero',
    body: [
      'When a check cannot grade your site — usually because a firewall blocked it — we say so instead of inventing a number. That "withheld" result was still being averaged into your overall score as a zero, which pulled the total down for a check that had explicitly declined to score you.',
      'Withheld checks are now left out of the average entirely. If your overall score went up, nothing on your site changed; the earlier number was unfairly low. We now also show how many checks the score is based on, so a score built on 12 checks is never mistaken for one built on 13.',
    ],
    // Aggregate-only: the free single-tool pages never averaged anything.
    tools: ['platform-dashboard'],
  },
  {
    id: 'soft-404-scoring-2026-07-20',
    date: '2026-07-20',
    displayDate: '20 July 2026',
    title: 'Scoring methodology updated',
    body: [
      'We fixed how the AI Crawl Check scores a site that returns a normal web page instead of a proper "404 not found" when a file (like llms.txt or robots.txt) is missing. We used to treat that as "couldn\'t check" and drop it from your score; it actually means the file simply isn\'t there, so it now counts as missing — the same as any other site without one.',
      'If your score went down, nothing on your site changed for the worse. The old number was too generous, and the new one matches what any site with the same setup would get. The way to raise it is what it always was: add the missing file.',
    ],
    tools: ['crawl-check', 'platform-dashboard'],
  },
]

/** The notices that apply to a given tool, newest first. */
export function noticesForTool(toolSlug: string): ScoringNotice[] {
  return SCORING_NOTICES.filter(n => n.tools.includes(toolSlug)).sort((a, b) =>
    b.date.localeCompare(a.date)
  )
}

/** Canonical archive link, optionally anchored to one notice. */
export function methodologyHref(noticeId?: string): string {
  return noticeId
    ? `/platform/methodology#${noticeId}`
    : '/platform/methodology'
}
